import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { provideNgIdle } from '@ng-idle/core';
import { provideNgIdleKeepalive } from '@ng-idle/keepalive';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { clearStateMetaReducer } from './core/store/meta-reducers/clear-state.meta-reducer';
import { resetPatientStateMetaReducer } from './core/store/meta-reducers/reset-patient-state.meta-reducer';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './core/functions/msal-factories';
import { AppInsightsInterceptor } from './core/interceptors/app-insights-interceptor';
import { ErrorInterceptor } from './core/interceptors/error-interceptor/error-interceptor';
import { HttpTimeoutInterceptor } from './core/interceptors/http-timeout-interceptor';
import { CustomRouterStateSerializer } from './core/router/router-state-serializer';
import { AppInsightsService } from './core/services/app-insights/app-insights.service';
import { AuthService } from './core/services/auth/auth.service';
import { ConfigService } from './core/services/config/config.service';
import { AppDialogEffects } from './core/store/effects/app-dialog.effects';
import { AppNavigationEffects } from './core/store/effects/app-navigation.effects';
import { AppEffects } from './core/store/effects/app.effects';
import { appReducer } from './core/store/reducer/app.reducer';
import { NotificationPreferencesEffects } from './notification-preferences/store/effects/notification-preference.effects';
import {
  notificationPreferenceFeatureKey,
  notificationPreferenceReducer,
} from './notification-preferences/store/reducer/notification-preference.reducer';
import { TourProgressEffects } from './tours/store/effects/tour-progress.effects';
import { tourProgressFeatureKey, tourProgressReducer } from './tours/store/reducer/tour-progress.reducer';
import { UserEffects } from './user/store/effects/user.effects';
import { userReducer } from './user/store/reducer/user.reducer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideNgIdle(),
    provideNgIdleKeepalive(),
    provideStore({ router: routerReducer }, { metaReducers: [clearStateMetaReducer, resetPatientStateMetaReducer] }),
    provideRouterStore({ serializer: CustomRouterStateSerializer }),
    provideState({ name: 'app', reducer: appReducer }),
    provideState({ name: 'user', reducer: userReducer }),
    provideState({ name: tourProgressFeatureKey, reducer: tourProgressReducer }),
    // User-scoped, not patient-scoped: notification consent is keyed on the portal user, so the
    // slice lives here rather than in PATIENT_FEATURE_PROVIDERS and survives a patient switch.
    provideState({ name: notificationPreferenceFeatureKey, reducer: notificationPreferenceReducer }),
    provideEffects([
      AppEffects,
      AppNavigationEffects,
      AppDialogEffects,
      UserEffects,
      TourProgressEffects,
      NotificationPreferencesEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true,
    }),
    provideAnimationsAsync(),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const configService = inject(ConfigService);
      const router = inject(Router);
      const appInsightsService = inject(AppInsightsService);
      await configService.initialize();
      appInsightsService.initialize(router);
      authService.initialize();
    }),
    // Order is load-bearing: the first registered interceptor is outermost. HttpTimeoutInterceptor
    // must stay LAST so it sits innermost, closest to the XHR — that way its HttpTimeoutError is
    // thrown inside AppInsightsInterceptor and ErrorInterceptor (so both still classify and route
    // it), and unsubscribing actually cancels the request. It also keeps MSAL token acquisition
    // outside the budget, so an interactive consent redirect is never mistaken for a hung request.
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AppInsightsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpTimeoutInterceptor, multi: true },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ConfigService],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ConfigService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
